import { useEffect, useRef, useState, type FormEvent } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { Msg91WidgetConfig } from "../config";
import { loadMsg91Widget, WidgetError, type Msg91Widget } from "../auth/msg91Widget";
import { HumanCheck } from "../components/HumanCheck";
import {
  isTestCodeRequest,
  looksLikeEmail,
  maskMobile,
  normalizeIndianMobile,
  sendLoginCode,
  sendTestCode,
  startEmail,
  testCodeAccessToken,
} from "../api/login";
import { isTestMobile } from "../domain/testMobile";
import { CodeChannelChoice, codeChannels, codeSentLine, type CodeChannel } from "../components/CodeChannelChoice";

const TEST_NOTE = "Test number: no SMS is sent. Read the code in your operator's Reports › SMS log (status “TEST: not sent”).";
import {
  describeRules, forgotWithEmailCode, forgotWithUsername, forgotWithWidget, passwordProblem, resetPassword,
  type PasswordRules, type ResetProofOutcome,
} from "../api/password";
import { PasswordField } from "../components/PasswordField";

/**
 * "Forgot password?" on the sign-in screen (owner 2026-10-22): staff,
 * operators and sub-operators prove who they are with an SMS code to their
 * VERIFIED mobile, or a code to their VERIFIED email, then choose a new
 * password, then sign in with it. radaccess loginpassword.go.
 *
 * 🔑 This replaces the hosted ZITADEL "forgot password" link: that one mails a
 * code even to an address that was never verified.
 */

type Step = "start" | "sms_code" | "email_code" | "username" | "new" | "done";

/** Seconds before "Resend code" unlocks after an SMS (owner 2026-10-23: every send-code screen waits, then allows a resend). */
const SMS_RESEND_AFTER = 35;


/** What the person is told after a proof attempt that did not prove. */
export function proofMessage(r: ResetProofOutcome): string {
  switch (r.kind) {
    case "no_account":
      return "No active AnkhOS login uses this number with a verified mobile. Try your email, or ask your operator.";
    case "username_mismatch":
      return "That username does not belong to this mobile number. Check it and try again.";
    case "code_rejected":
      return "That code is not right, or it was already used. Ask for a new code.";
    case "busy":
      return "The SMS service is busy. Wait a minute and try again.";
    case "rate_limited":
      return `Too many attempts. Try again in ${Math.ceil(r.retryAfter / 60)} minute(s).`;
    case "not_active":
      return "This login is not active. Ask your operator to enable it.";
    case "expired":
      return "This step took too long. Start again.";
    default:
      return "That could not be completed. Try again in a moment.";
  }
}

export function ForgotPassword(props: {
  authRequestId: string;
  widgetCfg: Msg91WidgetConfig | null | undefined;
  onBack: () => void;
  onExpired: () => void;
}) {
  const { authRequestId, widgetCfg } = props;
  const [step, setStep] = useState<Step>("start");
  const [by, setBy] = useState<"sms" | "email">(widgetCfg ? "sms" : "email");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [username, setUsername] = useState("");
  const [masked, setMasked] = useState("");
  const [reqId, setReqId] = useState("");
  const [emailTicket, setEmailTicket] = useState("");
  const [proofTicket, setProofTicket] = useState("");
  const [resetTicket, setResetTicket] = useState("");
  const [rules, setRules] = useState<PasswordRules | null>(null);
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Where the code went (owner 2026-10-25), and for a test number that it is in
  // the SMS log, not on a phone (owner 2026-10-24).
  const [codeInfo, setCodeInfo] = useState("");
  const [channel, setChannel] = useState<CodeChannel>("sms");
  const widgetRef = useRef<Msg91Widget | null>(null);
  const [resendUntil, setResendUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const resendIn = Math.max(0, Math.ceil((resendUntil - now) / 1000));
  const setResendIn = (seconds: number) => {
    const t = Date.now();
    setNow(t);
    setResendUntil(t + seconds * 1000);
  };

  // Counted against a deadline, as on SignIn: a throttled background tab still
  // reads the right number when it wakes.
  useEffect(() => {
    if (resendUntil <= now) return;
    const t = window.setTimeout(() => setNow(Date.now()), 1000);
    return () => window.clearTimeout(t);
  }, [resendUntil, now]);

  const run = async (f: () => Promise<void>) => {
    setBusy(true);
    setError("");
    try {
      await f();
    } finally {
      setBusy(false);
    }
  };

  const startOver = () => {
    setStep("start");
    setCode("");
    setError("");
    setResetTicket("");
    setProofTicket("");
  };

  const afterProof = (r: ResetProofOutcome) => {
    if (r.kind === "proven") {
      setResetTicket(r.resetTicket);
      setRules(r.rules);
      setStep("new");
      return;
    }
    if (r.kind === "username_needed") {
      setProofTicket(r.ticket);
      setStep("username");
      return;
    }
    if (r.kind === "expired") {
      startOver();
    }
    setError(proofMessage(r));
    // A widget token is single-use: after a refusal only a new code helps.
    if (step === "sms_code" && (r.kind === "code_rejected" || r.kind === "no_account")) startOver();
  };

  const sendSms = () =>
    run(async () => {
      const e164 = normalizeIndianMobile(mobile);
      if (e164 === null || !widgetCfg) {
        setError("Enter a 10-digit Indian mobile number.");
        return;
      }
      // ONE code from radaccess on the chosen channels (owner 2026-10-25); the
      // widget only when radaccess cannot make codes.
      const c = await sendLoginCode({ mobile: e164, channels: codeChannels(channel), purpose: "reset_password" });
      if (c.kind === "sent") {
        widgetRef.current = null;
        setReqId(c.requestId);
        setMasked(maskMobile(e164));
        setCode("");
        setResendIn(c.resendIn);
        setCodeInfo(codeSentLine(maskMobile(e164), c.sms, c.email) + (isTestMobile(e164.slice(3)) ? " " + TEST_NOTE : ""));
        setStep("sms_code");
        return;
      }
      if (c.kind === "busy") {
        setError(`Too many codes. Try again in ${Math.ceil(c.retryAfter / 60)} minute(s).`);
        return;
      }
      if (c.kind === "refused") {
        setError(c.message);
        return;
      }
      // A TEST number (test console only) cannot receive an SMS: radaccess
      // writes the code to the operator's SMS log instead.
      if (isTestMobile(e164.slice(3))) {
        const t = await sendTestCode(e164);
        if (t.kind === "sent") {
          widgetRef.current = null;
          setReqId(t.requestId);
          setMasked(maskMobile(e164));
          setCode("");
          setResendIn(t.resendIn);
          setCodeInfo("Test number: no SMS is sent. Read the code in your operator's Reports › SMS log (status “TEST: not sent”).");
          setStep("sms_code");
        } else if (t.kind === "busy") {
          setError(`Too many codes. Try again in ${Math.ceil(t.retryAfter / 60)} minute(s).`);
        } else {
          setError(t.kind === "refused" ? t.message : "The code could not be sent. Try again.");
        }
        return;
      }
      setCodeInfo("");
      try {
        const w = await loadMsg91Widget(widgetCfg);
        widgetRef.current = w;
        setReqId(await w.send(e164));
        setMasked(maskMobile(e164));
        setCode("");
        setResendIn(SMS_RESEND_AFTER);
        setStep("sms_code");
      } catch (err) {
        setError(err instanceof WidgetError && err.throttled ? "The SMS service is busy. Wait a minute and try again." : "The code could not be sent. Try again.");
      }
    });

  const verifySms = () =>
    run(async () => {
      const w = widgetRef.current;
      const testRequest = isTestCodeRequest(reqId);
      if (!reqId || (!w && !testRequest)) {
        startOver();
        return;
      }
      let token: string;
      try {
        token = testRequest ? testCodeAccessToken(reqId, code) : await w!.verify(code, reqId);
      } catch {
        setError("That code is not right. Check it and try again.");
        setCode("");
        return;
      }
      afterProof(await forgotWithWidget({ authRequestId, mobile, accessToken: token }));
    });

  const sendEmail = () =>
    run(async () => {
      if (!looksLikeEmail(email)) {
        setError("Enter the email address of your login.");
        return;
      }
      const r = await startEmail({ authRequestId, email });
      if (r.kind === "sent") {
        setEmailTicket(r.ticket);
        setMasked(r.emailMasked);
        setCode("");
        setResendIn(r.resendAfter);
        setStep("email_code");
        return;
      }
      if (r.kind === "expired") props.onExpired();
      else if (r.kind === "email_shared") setError("More than one login uses this email. Use your mobile number instead.");
      else if (r.kind === "rate_limited") setError(`Too many attempts. Try again in ${Math.ceil(r.retryAfter / 60)} minute(s).`);
      else setError("The code could not be sent. Try again.");
    });

  const resendSms = (channel: "sms" | "voice") => {
    if (isTestCodeRequest(reqId)) return sendSms();
    return run(async () => {
      const w = widgetRef.current;
      if (!w || !reqId) {
        startOver();
        return;
      }
      try {
        await w.resend(channel, reqId);
        setCode("");
        setResendIn(SMS_RESEND_AFTER);
      } catch (err) {
        setError(err instanceof WidgetError && err.throttled ? "The SMS service is busy. Wait a minute and try again." : "The code could not be sent again. Try again.");
      }
    });
  };

  const verifyEmail = () =>
    run(async () => afterProof(await forgotWithEmailCode({ authRequestId, ticket: emailTicket, code })));

  const sendUsername = () =>
    run(async () => afterProof(await forgotWithUsername({ authRequestId, ticket: proofTicket, username })));

  const problem = passwordProblem(next, rules);
  const mismatch = again !== "" && again !== next;

  const save = () =>
    run(async () => {
      const r = await resetPassword({ authRequestId, resetTicket, newPassword: next });
      if (r.kind === "reset" || r.kind === "unchanged") {
        setNext("");
        setAgain("");
        setStep("done");
        return;
      }
      if (r.kind === "policy") {
        if (r.rules) setRules(r.rules);
        setError(`Not accepted: ${r.problem}.`);
      } else if (r.kind === "expired") {
        startOver();
        setError("This step took too long, or the link was already used. Start again.");
      } else if (r.kind === "locked") setError("This login is locked after too many attempts. Ask your operator to unlock it.");
      else if (r.kind === "rate_limited") setError(`Too many attempts. Try again in ${Math.ceil(r.retryAfter / 60)} minute(s).`);
      else setError("That could not be completed. Try again in a moment.");
    });

  const submit = (f: () => Promise<void>) => (e: FormEvent) => {
    e.preventDefault();
    if (!busy) void f();
  };

  const back = (
    <Typography variant="body2" color="text.secondary">
      <Link component="button" type="button" onClick={props.onBack}>
        Back to sign in
      </Link>
    </Typography>
  );
  const notice = error ? (
    <Alert severity="error" data-testid="forgot-error">{error}</Alert>
  ) : codeInfo && step === "sms_code" ? (
    <Alert severity="info" data-testid="forgot-test-code">
      {codeInfo}
    </Alert>
  ) : null;

  if (step === "done") {
    return (
      <Stack spacing={2}>
        <Alert severity="success" data-testid="forgot-done">
          Your password is changed. Sign in with your new password.
        </Alert>
        <Button variant="contained" size="large" onClick={props.onBack}>
          Sign in
        </Button>
      </Stack>
    );
  }

  if (step === "new") {
    return (
      <form onSubmit={submit(save)} noValidate>
        <Stack spacing={2}>
          <Typography variant="body2">Choose a new password.</Typography>
          {notice}
          <PasswordField label="New password" autoComplete="new-password" value={next} required fullWidth autoFocus
            onChange={(e) => setNext(e.target.value)} error={next !== "" && Boolean(problem)}
            helperText={next !== "" && problem ? problem : rules ? `Use ${describeRules(rules)}.` : " "}
            slotProps={{ htmlInput: { "data-testid": "new-password" } }} />
          <PasswordField label="New password again" autoComplete="new-password" value={again} required fullWidth
            onChange={(e) => setAgain(e.target.value)} error={mismatch}
            helperText={mismatch ? "The two passwords are not the same." : " "}
            slotProps={{ htmlInput: { "data-testid": "new-password-again" } }} />
          <Button type="submit" variant="contained" size="large" disabled={busy || !next || !again || Boolean(problem) || mismatch}>
            {busy ? "Saving…" : "Save new password"}
          </Button>
          {back}
        </Stack>
      </form>
    );
  }

  if (step === "username") {
    return (
      <form onSubmit={submit(sendUsername)} noValidate>
        <Stack spacing={2}>
          <Typography variant="body2">
            More than one login uses this mobile number. Enter the username of the login whose password you forgot.
          </Typography>
          {notice}
          <HumanCheck action="forgot" />
          <TextField label="Username" autoComplete="username" value={username} required fullWidth autoFocus
            onChange={(e) => setUsername(e.target.value)} slotProps={{ htmlInput: { "data-testid": "forgot-username" } }} />
          <Button type="submit" variant="contained" size="large" disabled={busy || !username.trim()}>
            {busy ? "Checking…" : "Continue"}
          </Button>
          {back}
        </Stack>
      </form>
    );
  }

  if (step === "sms_code" || step === "email_code") {
    const sms = step === "sms_code";
    return (
      <form onSubmit={submit(sms ? verifySms : verifyEmail)} noValidate>
        <Stack spacing={2}>
          <Typography variant="body2">
            Enter the code we sent to {masked || (sms ? "your mobile" : "your email")}.
          </Typography>
          {notice}
          <HumanCheck action="forgot" />
          <TextField label="Code" value={code} required fullWidth autoFocus autoComplete="one-time-code"
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
            slotProps={{ htmlInput: { inputMode: "numeric", "data-testid": "forgot-code" } }} />
          <Button type="submit" variant="contained" size="large" disabled={busy || code.length < 4}>
            {busy ? "Checking…" : "Continue"}
          </Button>
          <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
            <Button size="small" data-testid="forgot-resend" disabled={busy || resendIn > 0}
              onClick={() => void (sms ? resendSms("sms") : sendEmail())}>
              {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
            </Button>
            {sms && !isTestCodeRequest(reqId) && (
              <Button size="small" disabled={busy || resendIn > 0} onClick={() => void resendSms("voice")}>
                Call me instead
              </Button>
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            <Link component="button" type="button" onClick={startOver}>
              {sms ? "Change number" : "Change email"}
            </Link>
            {" · "}
            <Link component="button" type="button" onClick={props.onBack}>
              Back to sign in
            </Link>
          </Typography>
        </Stack>
      </form>
    );
  }

  return (
    <form onSubmit={submit(by === "sms" ? sendSms : sendEmail)} noValidate>
      <Stack spacing={2}>
        <Typography variant="body2" data-testid="forgot-start">
          {by === "sms"
            ? "Forgot your password? Enter the mobile number of your login. We will send it a code."
            : "Forgot your password? Enter the email address of your login. It must be a verified address."}
        </Typography>
        {notice}
        {/* Owner 2026-10-24: a human check on every way in. The SMS start goes to
            MSG91 directly; the email start and every later step reach radaccess. */}
        <HumanCheck action="forgot" />
        {by === "sms" ? (
          <TextField label="Mobile number" value={mobile} required fullWidth autoFocus autoComplete="tel" inputMode="tel"
            onChange={(e) => setMobile(e.target.value)} slotProps={{ htmlInput: { "data-testid": "forgot-mobile" } }} />
        ) : null}
        {by === "sms" ? (
          <CodeChannelChoice value={channel} onChange={setChannel} disabled={busy} />
        ) : (
          <TextField label="Email" type="email" value={email} required fullWidth autoFocus autoComplete="email"
            onChange={(e) => setEmail(e.target.value)} slotProps={{ htmlInput: { "data-testid": "forgot-email" } }} />
        )}
        <Button type="submit" variant="contained" size="large" disabled={busy || (by === "sms" ? !mobile.trim() : !email.trim())}>
          {busy ? "Sending…" : "Send code"}
        </Button>
        <Typography variant="body2" color="text.secondary">
          {widgetCfg && (
            <>
              <Link component="button" type="button" onClick={() => { setError(""); setBy(by === "sms" ? "email" : "sms"); }}>
                {by === "sms" ? "Use my email instead" : "Use my mobile instead"}
              </Link>
              {" · "}
            </>
          )}
          <Link component="button" type="button" onClick={props.onBack}>
            Back to sign in
          </Link>
        </Typography>
      </Stack>
    </form>
  );
}
